import { type companyagreementApi } from 'app/src/services/welfareportal'
import { euroFormatterIt, getValueFromTypology } from 'app/src/shared/helpers'
import { useTranslation, useWebAppContext } from 'app/src/shared/hooks'
import { Card } from 'primereact/card'
import { Dropdown } from 'primereact/dropdown'
import type { SelectItem, SelectItemOptionsType } from 'primereact/selectitem'
import { useMemo, type ReactElement } from 'react'

export type ProductVariantListPropsType = {
  variants?: companyagreementApi.Variant[]
  multiPurchase: boolean
  onChangeSelectedVariant?: (variant?: companyagreementApi.Variant) => void
  selectedVariant?: companyagreementApi.Variant
}
export function ProductVariantList(props: ProductVariantListPropsType): ReactElement {
  const context = useWebAppContext()
  const { t } = useTranslation('productDetail')
  const variantDropDownTreshold = useMemo(
    () => context.portalConfiguration.showCaseConfig?.productCardTreshold ?? 4,
    [context.portalConfiguration?.showCaseConfig?.productCardTreshold],
  )

  const variantSorting = (vara: companyagreementApi.Variant, varb: companyagreementApi.Variant): number => {
    if (vara.weight !== varb.weight) return vara.weight! > varb.weight! ? -1 : 1
    else {
      if (
        vara.priceDetails?.facePrice !== varb.priceDetails?.facePrice &&
        vara?.priceDetails?.facePrice &&
        varb?.priceDetails?.facePrice
      ) {
        return vara?.priceDetails?.facePrice < varb?.priceDetails?.facePrice ? -1 : 1
      } else {
        return vara?.variantName?.localeCompare(varb?.variantName) ?? -1
      }
    }
  }
  const options: SelectItemOptionsType = useMemo(
    () =>
      props.variants?.sort(variantSorting)?.map((variant) => ({
        key: variant.variantId,
        label: variant.variantName,
        value: variant,
      })) ?? [],
    [props.variants],
  )
  const optionTemplate = (opt: SelectItem) => {
    const variantName = !opt ? 'N.D.' : opt?.value?.variantName

    let variantNameAriaLabel = variantName
    if (opt?.value?.priceDetails) {
      if (opt?.value?.priceDetails.facePrice === 0)
        variantNameAriaLabel = `${variantNameAriaLabel}... ${t('ProductDetail.facePriceGratis')}`
      else
        variantNameAriaLabel = `${variantNameAriaLabel}... ${t('ProductDetail.productVariantNameAriaLabel')} ${euroFormatterIt.format(opt?.value?.priceDetails.facePrice)}`
    }

    return (
      <div className='flex max-h-[120px] flex-row items-center px-[10px] py-[2px]' aria-label={variantNameAriaLabel}>
        <div aria-hidden>
          <p className='text-xl font-normal text-black'>{variantName}</p>
          <p className='m-0 p-0 text-xl font-bold leading-[40px] text-[var(--primary-color)]'>{getFacePrice(opt?.value)}</p>
        </div>
      </div>
    )
  }

  const getFacePrice = (variant: companyagreementApi.Variant): string => {
    const discountType = variant?.priceDetails?.discountType
    const discountValue = variant?.priceDetails?.discountValue
    if (!variant?.priceDetails) {
      return ''
    }
    if (variant?.priceDetails?.discountType) {
      if (discountValue === 0) return t('ProductDetail.facePriceGratis')
      return getValueFromTypology(discountType, discountValue ?? 0)
    }
    return variant.priceDetails && variant.priceDetails.facePrice !== 0
      ? euroFormatterIt.format(variant.priceDetails.facePrice)
      : t('ProductDetail.facePriceGratis')
  }

  return (
    <div className='mt-[20px]' data-testid='product-variant-list'>
      {!props.multiPurchase && props.variants && props.variants.length > 1 && (
        <div>
          <div className='text-[20px] font-semibold' data-testid='variant-list-title'>
            {t('ProductDetail.productVariantListDropDownTitle')}
          </div>
          {!props.variants[0]?.priceDetails?.discountType && (
            <div className='mt-[10px] text-[16px] leading-[24px]' data-testid='variant-list-description'>
              {t('ProductDetail.productVariantListDropDownDescription')}
            </div>
          )}
          <div role='radiogroup' className='my-[30px] flex flex-wrap gap-2' data-testid='variant-radiogroup'>
            {props.variants.length <= variantDropDownTreshold ? (
              props.variants.sort(variantSorting).map((variant) => {
                const backgroundColor =
                  variant.variantId === props.selectedVariant?.variantId ? 'bg-[var(--primary-color)]' : 'bg-[#d9ebff]'
                const borderColor =
                  variant.variantId === props.selectedVariant?.variantId ? 'border-[var(--primary-color)]' : 'border-[#d9ebff]'
                const fontColor =
                  variant.variantId === props.selectedVariant?.variantId ? 'text-[#FFFFFF]' : 'text-[var(--primary-color)]'
                const titleColor = variant.variantId === props.selectedVariant?.variantId ? 'text-[#FFFFFF]' : 'text-[#1A1C1E]'

                return (
                  <div
                    key={`variantcard_${variant.variantId}`}
                    className='w-full lg:max-w-[420px]'
                    data-testid={`variant-card-${variant.variantId}`}
                  >
                    <Card
                      pt={{
                        body: {
                          className: 'pr-[4px] pl-[22px] py-[8px] m-0',
                        },
                        content: { className: 'p-0 m-0' },
                        root: {
                          role: 'radio',
                          tabIndex: 0,
                          className: 'cursor-pointer',
                          'aria-checked': props.selectedVariant && props.selectedVariant.variantId === variant.variantId,
                        },
                      }}
                      aria-checked={props.selectedVariant && props.selectedVariant.variantId === variant.variantId}
                      className={`${backgroundColor} ${borderColor} min-h-[188px] w-full p-0 md:w-[416px]`}
                      onKeyDown={(event) => {
                        if (event.key === ' ' || event.key === 'Enter') {
                          props.onChangeSelectedVariant!(variant)
                        }
                      }}
                      onClick={() => props.onChangeSelectedVariant!(variant)}
                    >
                      <div
                        className={`line-clamp-3 h-[100px] max-h-[100px] min-h-[100px] break-words pr-[30px] pt-[20px] text-[18px] font-semibold leading-normal ${titleColor}`}
                        data-testid='variant-name'
                      >
                        {variant.variantName}
                      </div>
                      <div
                        className={`text-right text-[48px] font-semibold leading-normal ${fontColor}`}
                        data-testid='variant-price'
                      >
                        {getFacePrice(variant)}
                      </div>
                    </Card>
                  </div>
                )
              })
            ) : (
              <div className='grid w-full grid-cols-12'>
                <Dropdown
                  className='col-span-10 col-start-2 border-[var(--primary-color)] hover:bg-[#f0f8ff] md:col-span-7 md:col-start-3'
                  itemTemplate={optionTemplate}
                  valueTemplate={optionTemplate}
                  value={props.selectedVariant}
                  onChange={(ev) => props.onChangeSelectedVariant!(ev.value)}
                  inputMode={'none'}
                  placeholder=''
                  id='variantDropDown'
                  data-testid='variant-dropdown'
                  options={options}
                  pt={{ item: { className: 'hover:bg-[#f0f8ff]' } }}
                  // styles={variantsDropdownStyles}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
